/**
 * AthleteNameLink
 * ---------------
 * Shared, drop-in component that turns an athlete name into a router link
 * pointing at `/coach/athletes/{id}` — the athlete dossier page.
 *
 * Why this exists:
 *   Per Sachin (2026-02): "When I find an athlete anywhere in the back end,
 *   I should be able to click and go straight to their profile. That should
 *   be a general rule everywhere on the back end."
 *
 * Behaviour:
 *   - Renders a styled <Link> when `athleteId` is present.
 *   - Falls back to plain text (e.g. for dropped signups, applications, or
 *     anonymous leads where no athlete record exists yet).
 *   - Stops click propagation so the link still works inside parent rows
 *     that have their own onClick handlers (e.g. expandable lead cards).
 *
 * Props:
 *   - athleteId : string  | undefined  →  user-id of the athlete record
 *   - name      : string                →  display name
 *   - className : string  (optional)    →  override classes
 *   - testid    : string  (optional)    →  data-testid value
 *   - as        : string  (optional)    →  wrapping tag if no id (default span)
 */
import { Link } from "react-router-dom";

const DEFAULT_CLS =
    "font-semibold text-white hover:text-brand-red underline-offset-2 hover:underline transition-colors cursor-pointer";

export default function AthleteNameLink({
    athleteId,
    name,
    className,
    testid,
    as: Tag = "span",
}) {
    const display = (name || "").toString().trim() || "Athlete";

    if (!athleteId) {
        return (
            <Tag data-testid={testid} className={className || "text-white"}>
                {display}
            </Tag>
        );
    }

    return (
        <Link
            to={`/coach/athletes/${athleteId}`}
            data-testid={testid}
            onClick={(e) => e.stopPropagation()}
            className={className || DEFAULT_CLS}
        >
            {display}
        </Link>
    );
}
